import * as React from "react";
import "./styles.scss";
import { Button, Form, Icon, Label, Modal, Popup } from "semantic-ui-react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  PlayerCard,
  PlayersServices,
  PlayerCardDefaultables,
  Player,
  PlayerCardCreateRequestBody,
} from "../../../services/bedrock";
import PaginatedDropdown from "../../../components/PaginatedDropdown";
import { CancelToken } from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { PlayerCardSettingsContext } from "../Contexts";
import SendNotificationConfirmation from "../SendNotificationConfirmation";
import AddPlayerCardWarningModal from "./AddPlayerCardWarningModal";

interface AddPlayerCardModalProps {
  open: boolean;
  onClose: (playerCards?: PlayerCard[]) => void;
}

const AddPlayerCardModal: React.FC<AddPlayerCardModalProps> = ({ open, onClose }) => {
  const [loadedPlayers, setLoadedPlayers] = useState<{ [id: number]: Player }>({});
  const [playersWithoutAccess, setPlayersWithoutAccess] = useState<Player[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const [showSendNotificationConfirmation, setShowSendNotificationConfirmation] = useState(false);
  const { PlayerCards, Templates, Themes } = useContext(PlayerCardSettingsContext);
  const methods = useForm<PlayerCardCreateRequestBody>({
    defaultValues: {
      playerIds: [],
      templateId: PlayerCardDefaultables.default.id,
      themeId: PlayerCardDefaultables.default.id,
      sendNotification: true,
      grantPlayerCardAccess: false,
      grantPlayerCardAccessGlobally: false,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
    getValues,
  } = methods;

  useEffect(() => {
    if (open) {
      let playerIds: number[] = [];
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const filterPlayerId = searchParams.get("playerId");
        if (filterPlayerId) {
          const pid = Number(filterPlayerId);
          playerIds = [pid];
          PlayersServices.getPlayer(pid, ["allowPlayerAccessToPlayerCardsPermission"])
            .then(player => {
              if (player) {
                setLoadedPlayers(prev => ({ ...prev, [pid]: player }));
              }
            })
            .catch(console.error);
        }
      }
      reset({
        playerIds,
        templateId: PlayerCardDefaultables.default.id,
        themeId: PlayerCardDefaultables.default.id,
        sendNotification: true,
        grantPlayerCardAccess: false,
        grantPlayerCardAccessGlobally: false,
      });
    }
  }, [open, reset]);

  const cancel = () => onClose();

  const save = (sendNotification: boolean) => {
    handleSubmit(data => {
      if (!isDirty) return;

      data.sendNotification = sendNotification;

      return PlayerCards.createPlayerCard(data).then(onClose);
    })();
  };

  const handleSaveClick = useCallback(() => {
    const playerIds = getValues("playerIds") || [];
    const playersWithoutAccess = playerIds
      .map(id => {
        const player = loadedPlayers[id];
        if (player && !player.allowPlayerAccessToPlayerCardsPermission) return player;
        else return null;
      })
      .filter(Boolean) as Player[];
    if (playersWithoutAccess.length > 0) {
      setPlayersWithoutAccess(playersWithoutAccess);
      setShowWarning(true);
    } else {
      setShowSendNotificationConfirmation(true);
    }
  }, [loadedPlayers]);

  const loadPlayerData = async (page: number, search?: string, cancelToken?: CancelToken) => {
    const data = await PlayersServices.getPlayers(
      { search, page, hasPlayerCard: false, extraFields: ["allowPlayerAccessToPlayerCardsPermission"] },
      { cancelToken }
    );
    const loadedPlayers: { [id: number]: Player } = {};
    const content = data.content.map(player => {
      const searchValues = [player.fullName, player.email].filter(Boolean) as string[];
      loadedPlayers[player.id] = player;
      return {
        key: player.id,
        text: player.fullName,
        value: player.id,
        searchValues,
      };
    });
    setLoadedPlayers(prev => ({ ...prev, ...loadedPlayers }));
    return { ...data, content };
  };

  const handleWarningClosed = useCallback(
    (confirm: boolean) => {
      setShowWarning(false);
      if (!confirm) return;

      const grantPlayerCardAccess =
        getValues("grantPlayerCardAccess") || getValues("grantPlayerCardAccessGlobally") || false;
      var canSendNotification = grantPlayerCardAccess;
      if (!canSendNotification) {
        const playerIds = getValues("playerIds") || [];
        canSendNotification = playerIds.some(id => {
          const player = loadedPlayers[id];
          return player && player.allowPlayerAccessToPlayerCardsPermission;
        });
      }
      if (canSendNotification) {
        setShowSendNotificationConfirmation(true);
      } else {
        save(false);
      }
    },
    [save]
  );

  const getPlayerName = () => {
    const playerIds = getValues("playerIds") || [];
    if (playerIds.length === 1) return loadedPlayers[playerIds[0]]?.fullName ?? "Player";
    else return "Players";
  };

  const handleSendNotificationConfirmationClosed = useCallback(
    (sendNotification: boolean | undefined) => {
      setShowSendNotificationConfirmation(false);
      if (sendNotification !== undefined) {
        save(sendNotification);
      }
    },
    [save]
  );

  return (
    <Modal open={open} size="small" className="addPlayerCardModal">
      <Modal.Header>Add New Player Card(s)</Modal.Header>
      <Modal.Content>
        <FormProvider {...methods}>
          <Form>
            <AddPlayerCardWarningModal
              open={showWarning}
              playersWithoutAccess={playersWithoutAccess}
              onClose={handleWarningClosed}
            />
            <SendNotificationConfirmation
              open={showSendNotificationConfirmation}
              playerName={getPlayerName()}
              onClose={handleSendNotificationConfirmationClosed}
            />

            <Controller
              name="playerIds"
              control={control}
              rules={{ required: "This is required" }}
              render={({ field, fieldState: { error } }) => {
                const onChange = (_e: any, data: any) => field.onChange(data.value);
                const { ref, ...effectiveField } = field;
                const selectedIds = (field.value as number[]) || [];
                const initialOptions = selectedIds
                  .map(id => {
                    const player = loadedPlayers[id];
                    if (!player) return null;
                    return {
                      key: player.id,
                      text: player.fullName,
                      value: player.id,
                    };
                  })
                  .filter(Boolean) as any[];
                return (
                  <Form.Field className="playerCardField" error={!!error}>
                    <div className="playerCardFieldLabel">
                      Player
                      <Popup
                        content={
                          <span>
                            Select player(s) for the new Player Card(s). <b>NOTE:</b> Players with Player Cards already
                            are excluded from this list.
                          </span>
                        }
                        trigger={<Icon name="question circle" />}
                      />
                    </div>
                    <PaginatedDropdown
                      {...effectiveField}
                      search
                      multiple
                      onChange={onChange}
                      loadData={loadPlayerData}
                      placeholder="Select Player(s)..."
                      initialOptions={initialOptions}
                    />
                    {error && (
                      <Label basic prompt pointing>
                        {error.message}
                      </Label>
                    )}
                  </Form.Field>
                );
              }}
            />
            <Controller
              name="templateId"
              control={control}
              rules={{ required: "This is required" }}
              render={({ field, fieldState: { error } }) => {
                const onChange = (_e: any, data: any) => field.onChange(data.value);
                const { ref, ...effectiveField } = field;
                return (
                  <Form.Field className="playerCardField" error={!!error}>
                    <div className="playerCardFieldLabel">
                      Template
                      <Popup
                        content={<span>Select a template for the new Player Card(s).</span>}
                        trigger={<Icon name="question circle" />}
                      />
                    </div>
                    <PaginatedDropdown
                      {...effectiveField}
                      search
                      onChange={onChange}
                      loadData={Templates.loadTemplateDropdownOptions}
                      placeholder="Select Template..."
                    />
                    {error && (
                      <Label basic prompt pointing>
                        {error.message}
                      </Label>
                    )}
                  </Form.Field>
                );
              }}
            />
            <Controller
              name="themeId"
              control={control}
              rules={{ required: "This is required" }}
              render={({ field, fieldState: { error } }) => {
                const onChange = (_e: any, data: any) => field.onChange(data.value);
                const { ref, ...effectiveField } = field;
                return (
                  <Form.Field className="playerCardField" error={!!error}>
                    <div className="playerCardFieldLabel">
                      Theme
                      <Popup
                        content={<span>Select a theme for the new Player Card(s).</span>}
                        trigger={<Icon name="question circle" />}
                      />
                    </div>
                    <PaginatedDropdown
                      {...effectiveField}
                      search
                      onChange={onChange}
                      loadData={Themes.loadThemeDropdownOptions}
                      placeholder="Select Theme..."
                    />
                    {error && (
                      <Label basic prompt pointing>
                        {error.message}
                      </Label>
                    )}
                  </Form.Field>
                );
              }}
            />
          </Form>
        </FormProvider>
      </Modal.Content>
      <Modal.Actions>
        <Button loading={isSubmitting} secondary content="Cancel" onClick={cancel} />
        <div className="grow" />
        <Button loading={isSubmitting} primary disabled={!isDirty} content="Save" onClick={handleSaveClick} />
      </Modal.Actions>
    </Modal>
  );
};
export default AddPlayerCardModal;
